export interface Verification {
  verificationId: string;
  status: string;
  profile: string;
  environment: string;
  /**
   * The applicant's name, already joined by the API. Note this differs from the
   * detail response, which splits it into applicantFirstName/applicantLastName.
   */
  fullName: string | null;
  checks: VerificationCheck[];
  /** Enum-style reason codes, e.g. "DATE_OF_BIRTH_MISMATCH". Empty when not declined. */
  declinedReasons: string[];
  createdAt: string;
  updatedAt: string;
}

/**
 * True when a verification has never been touched since creation — no
 * decision, no status change (createdAt === updatedAt). Shared by any grid
 * that lists Verification rows and wants to hide that noise rather than
 * listing records nothing has happened to yet.
 */
export function isUntouchedVerification(verification: Verification): boolean {
  return verification.createdAt === verification.updatedAt;
}

// TEMPORARY: names to scrub from every verification list grid. Requested as a
// quick hide for a specific record, not a real moderation/data-quality
// feature — remove this whole block (and its use in DashboardPage/KycPage)
// once that's handled properly (e.g. server-side, or deleting the record).
const TEMP_HIDDEN_FULL_NAMES = ["Fatima Majid"];

export function isTemporarilyHiddenVerification(verification: Verification): boolean {
  return TEMP_HIDDEN_FULL_NAMES.includes(verification.fullName ?? "");
}

export interface VerificationsResponse {
  items: Verification[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface VerificationDocument {
  documentType: string;
  issueDate: string;
  expiryDate: string;
  status: string;
}

// documentId isn't on VerificationDocument itself — it's resolved via this
// separate array, matched to a document by `type` === documentType.
export interface DocumentReference {
  id: string;
  type: string;
}

export interface DocumentImage {
  /** e.g. "PORTRAIT", "FRONT", "BACK". */
  side: string;
  url: string;
}

// Shape returned by GET /portal/api/verifications/{id}/documents/{documentId}/images.
export interface DocumentImagesResponse {
  type: string;
  images: DocumentImage[];
}

export interface VerificationCheck {
  /** e.g. "ADDRESS", "DOCUMENT", "PROFILE". */
  checkCode: string;
  status: string;
  isRequired: boolean;
  /** Enum-style reason codes, e.g. "DOCUMENT_EXPIRED". Empty when the check passed. */
  declineReasons: string[];
}

export interface VerificationDevice {
  deviceType: string;
  osName: string;
  osVersion: string;
  browserName: string;
  browserVersion: string;
}

export interface DatabaseScreeningMatch {
  name: string;
  /** e.g. "SANCTIONS", "PEP". */
  type: string;
  url: string;
  score: number;
}

export interface VerificationDatabaseScreening {
  matches: DatabaseScreeningMatch[];
}

// Shape returned by GET /portal/api/verifications/{id} — richer than the list
// item (adds applicant details and documents).
export interface VerificationDetail {
  verificationId: string;
  verificationStatus: string;
  applicantStatus: string;
  applicantFirstName: string | null;
  applicantLastName: string | null;
  documents: VerificationDocument[];
  documentReferences: DocumentReference[];
  /** Enum-style reason codes, e.g. "DATE_OF_BIRTH_MISMATCH". Empty when not declined. */
  declinedReasons: string[];
  checks: VerificationCheck[];
  device: VerificationDevice | null;
  databaseScreening: VerificationDatabaseScreening | null;
  createdAt: string;
  updatedAt: string;
}

export interface VerificationsActivitySummary {
  total: number;
  passed: number;
  failed: number;
  pending: number;
  /** Jurisdiction names the caller may filter by, e.g. ["Brasil", "Nevada", "Vegas"]. */
  availableJurisdictions?: string[];
}

export interface DeclineReasonOccurrence {
  /** Enum-style reason code, e.g. "DATE_OF_BIRTH_MISMATCH". */
  reason: string;
  count: number;
}

export interface VerificationsDeclineReasonsSummary {
  declined: number;
  withReason: number;
  withoutReason: number;
  reasonOccurrences: number;
  items: DeclineReasonOccurrence[];
}

export interface DeviceBreakdownItem {
  id: string;
  label: string;
  value: number;
}

export interface DeviceBreakdown {
  classified: number;
  breakdown: DeviceBreakdownItem[];
}

export interface VerificationsDevicesSummary {
  total: number;
  byDeviceType: DeviceBreakdown;
  byOs: DeviceBreakdown;
  byBrowser: DeviceBreakdown;
}

// Shape returned by GET /portal/api/verifications/summary.
export interface VerificationsSummary {
  activity: VerificationsActivitySummary | null;
  devices: VerificationsDevicesSummary | null;
  declineReasons: VerificationsDeclineReasonsSummary | null;
  /** Jurisdiction names the caller may filter by, e.g. ["Brasil", "Nevada", "Vegas"]. */
  availableJurisdictions?: string[];
}

/**
 * The jurisdiction filter's options come from the summary response itself — the
 * portal has no separate jurisdictions endpoint. It's read from the top level or
 * from inside `activity`, since the field travels with the activity summary and
 * either nesting satisfies the contract.
 */
export function getAvailableJurisdictions(
  summary: VerificationsSummary | null | undefined
): string[] {
  return summary?.availableJurisdictions ?? summary?.activity?.availableJurisdictions ?? [];
}
